export interface NoteTemplate {
  id: string;
  field: string;
  label: string;
  text: string;
  enabled: boolean;
}
export interface NoteTemplateLibrary {
  revision: number;
  updatedAt: string | null;
  templates: NoteTemplate[];
}

export const NOTE_FIELDS = [
  'safetyDetermination', 'sponsorComments', 'schoolComments',
  'employmentComments', 'abuseComments',
] as const;
const slotPattern = /\{\{([a-z][a-z0-9_]{0,39})\}\}/gu;
export function noteSlots(text: string): string[] {
  return [...new Set(Array.from(text.matchAll(slotPattern), (match) => match[1]))];
}
export function hasUnfilledNoteSlots(text: string): boolean {
  return /\{\{|\}\}/u.test(text);
}
export function validateNoteTemplates(input: unknown): NoteTemplate[] {
  if (!Array.isArray(input) || input.length > 40) throw new Error('Use no more than 40 templates.');
  const ids = new Set<string>();
  return input.map((source) => {
    if (!source || typeof source !== 'object') throw new Error('Invalid note template.');
    const item = source as Record<string, unknown>;
    const id = typeof item.id === 'string' ? item.id : '';
    const field = typeof item.field === 'string' ? item.field : '';
    const label = typeof item.label === 'string' ? item.label.normalize('NFKC').trim() : '';
    const text = typeof item.text === 'string' ? item.text.normalize('NFKC').trim() : '';
    if (!/^[a-zA-Z0-9-]{1,64}$/u.test(id) || ids.has(id)) throw new Error('Each template needs a unique ID.');
    if (field !== '*' && !NOTE_FIELDS.some((name) => name === field)) throw new Error('Choose a narrative question.');
    if (!label || label.length > 80 || !text || text.length > 1000 || typeof item.enabled !== 'boolean') {
      throw new Error('Provide a title of 1 to 80 characters and wording of 1 to 1,000 characters.');
    }
    if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/u.test(label + text)) throw new Error('Control characters are not allowed.');
    if (hasUnfilledNoteSlots(text.replace(slotPattern, '')) || noteSlots(text).length > 8) {
      throw new Error('Use up to 8 details such as {{observation}}, with lowercase letters and underscores.');
    }
    ids.add(id);
    return { id, field, label, text, enabled: item.enabled };
  });
}

// Structured starters ask for facts. Quick phrases require an explicit choice;
// none are inserted automatically or assert a favorable finding.
export function defaultNoteLibrary(): NoteTemplateLibrary {
  return { revision: 0, updatedAt: null, templates: [
    { id: 'observation-action-plan', field: '*', label: 'Observation, action, follow-up', enabled: true,
      text: 'Observed: {{observation}}\nAction taken: {{action_taken}}\nFollow-up: {{follow_up}}' },
    { id: 'reported-information', field: '*', label: 'Reported information', enabled: true,
      text: 'Reported by {{source}}: {{report}}\nVerification: {{verification}}\nFollow-up: {{follow_up}}' },
    { id: 'chart-safety', field: 'safetyDetermination', label: 'Safety: observation and response', enabled: true,
      text: 'Reported concern and source: {{reported_concern}}\nObserved: {{observation}}\nAction taken: {{action_taken}}\nFollow-up and responsible party: {{follow_up}}' },
    { id: 'chart-sponsor', field: 'sponsorComments', label: 'Sponsor: circumstances and follow-up', enabled: true,
      text: 'Living arrangement reported by {{source}}: {{living_arrangement}}\nReason given: {{reason}}\nVerification: {{verification}}\nAction and follow-up: {{follow_up}}' },
    { id: 'chart-education', field: 'schoolComments', label: 'Education: status and next steps', enabled: true,
      text: 'Education status reported by {{source}}: {{education_status}}\nEnrollment barrier or concern: {{barrier_or_concern}}\nVerification: {{verification}}\nAction and follow-up: {{follow_up}}' },
    { id: 'chart-employment', field: 'employmentComments', label: 'Employment: report and observations', enabled: true,
      text: 'Work circumstances reported by {{source}}: {{work_circumstances}}\nObserved: {{observation}}\nAction taken: {{action_taken}}\nFollow-up: {{follow_up}}' },
    { id: 'chart-concern', field: 'abuseComments', label: 'Concern: source, observations, response', enabled: true,
      text: 'Concern reported by {{source}}: {{reported_concern}}\nObserved: {{observation}}\nAction or notification completed: {{action_taken}}\nFollow-up and responsible party: {{follow_up}}' },
    { id: 'chart-not-assessed', field: '*', label: 'Not assessed this visit', enabled: true,
      text: 'Not assessed during this visit. No conclusion was made.' },
    { id: 'chart-verification-pending', field: '*', label: 'Verification pending', enabled: true,
      text: 'Information has not been independently verified. Follow-up is needed.' },
  ] };
}

/** Explicit manager action only. Never replace customized or disabled entries. */
export function addMissingChartNoteStarters(current: NoteTemplate[]): NoteTemplate[] {
  const ids = new Set(current.map((item) => item.id));
  const missing = defaultNoteLibrary().templates.filter((item) => !ids.has(item.id));
  if (current.length + missing.length > 40) throw new Error('Remove unused templates before adding the chart-note starters. The library limit is 40.');
  return [...current, ...missing];
}

export function isNoteDetailComplete(value: string | undefined): boolean {
  const text = value?.trim() ?? '';
  return Boolean(text) && text.length <= 1000 && !hasUnfilledNoteSlots(text);
}

export function fillNoteTemplate(template: string, details: Record<string, string>): string {
  const result = template.replace(slotPattern, (_match, name: string) => {
    const value = details[name]?.normalize('NFKC').trim();
    if (!isNoteDetailComplete(value)) throw new Error('Complete every detail before inserting.');
    return value!;
  });
  if (hasUnfilledNoteSlots(result) || result.length > 2000) throw new Error('The completed note exceeds 2,000 characters or contains an unfinished detail.');
  return result;
}
export function appendNoteText(current: string, text: string): string {
  const existing = current.trimEnd();
  const next = existing ? `${existing}\n\n${text}` : text;
  if (next.length > 2000) throw new Error('Not enough room. Shorten the note before inserting; nothing has been changed.');
  return next;
}
